<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content class="tw-pt-3 tw-pb-3.5">
        <base-breadcrumbs :breadcrumbs="breadcrumbs" />
      </base-content>
      <base-content class="tw-pt-0 tw-flex tw-items-center">
        <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">Business Profile</h1>
      </base-content>
    </div>
    <base-content class="tw-flex tw-flex-col">
      <base-card noPadding class="tw-relative">
        <div v-if="isProfileLoading" class="tw-bg-gray-100 tw-absolute tw-z-100 tw-opacity-50 tw-w-full tw-h-full md:tw-rounded-lg">
          <base-spinner class="tw-left-1/2 tw-top-1/2 tw-opacity-100" />
        </div>
        <base-content>
          <base-form ref="form" v-model="isFormValid">
            <div class="tw-mb-4 tw-font-normal">Business Information</div>
            <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
              <base-text-field label="Full Name" placeholder="Enter the name of the business" v-model="name" required />
              <base-advanced-select label="Business Type(s)" v-model="types" labelKey="label" multiple placeholder="Select one or more business types" :options="typeOptions" />
            </div>
            <div class="tw-mb-4 tw-mt-6 tw-font-normal">Main Business Address</div>
            <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
              <base-text-field label="Address Line 1" v-model="address.line1" required />
              <base-text-field label="Address Line 2 (optional)" v-model="address.line2" />
            </div>
            <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3">
              <base-text-field label="City" v-model="address.city" required />
              <base-select label="State" placeholder="Select state" :options="STATE_OPTIONS" label-key="name" v-model="address.state" required />
              <base-text-field label="Zip Code" v-model="address.zip" />
            </div>
            <div v-if="isPredictile()" class="tw-my-3 tw-w-1/2">
              <base-select label="Operating Model" v-model="operatingModel" placeholder="Select an operating model" :options="operatingModelOptions"> </base-select>
            </div>
            <div class="tw-flex tw-justify-end">
              <base-button type="flat" class="tw-mr-2" @click="loadPage" :disabled="isProfileLoading">Cancel</base-button>
              <base-button type="primary" @click="save" :disabled="!isFormValid" :loading="isProfileSaving">Save</base-button>
            </div>
          </base-form>
        </base-content>
      </base-card>
      <business-contacts class="tw-mt-4" :businessId="businessId"></business-contacts>
    </base-content>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { mapGetters } from "vuex";
import BusinessContacts from "@/views/business/profile/components/BusinessContacts";
import { STATE_OPTIONS } from "@/constants/constants";

export default {
  name: "BusinessProfile",
  components: {
    BusinessContacts,
  },
  props: {
    businessId: String,
  },
  data() {
    return {
      isProfileLoading: false,
      isProfileSaving: false,
      isFormValid: true,
      STATE_OPTIONS,
      name: null,
      selectedType: "",
      types: [],
      typeOptions: [
        { label: "Car Wash and Auto Detailing", value: "Car Wash and Auto Detailing" },
        { label: "Chiropractic", value: "Chiropractic" },
        { label: "Counselors & Therapists", value: "Counselors & Therapists" },
        { label: "Dry Cleaners", value: "Dry Cleaners" },
        { label: "Entertainment - Kids Activities", value: "Entertainment - Kids Activities" },
        { label: "Entertainment - Sports & Activities", value: "Entertainment - Sports & Activities" },
        { label: "Hair Care Services", value: "Hair Care Services" },
        { label: "Health, Fitness, and Wellness", value: "Health, Fitness, and Wellness" },
        { label: "Healthcare Providers", value: "Healthcare Providers" },
        { label: "Massage Therapy & Spas", value: "Massage Therapy & Spas" },
        { label: "Nail Care Services", value: "Nail Care Services" },
        { label: "Pet & Veterinary Services", value: "Pet & Veterinary Services" },
        { label: "Retail - Coffee Shops", value: "Retail - Coffee Shops" },
        { label: "Retail - Food & Beverage", value: "Retail - Food & Beverage" },
        { label: "Retail - Juice & Smoothie Shops", value: "Retail - Juice & Smoothie Shops" },
        { label: "Retail - Pizza Shops", value: "Retail - Pizza Shops" },
        { label: "Retail - Wine & Spirits Shops", value: "Retail - Wine & Spirits Shops" },
      ],
      address: {},
      operatingModel: "direct",
    };
  },
  computed: {
    ...mapGetters("role", ["roles", "isPredictile"]),
    breadcrumbs() {
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      return [{ title: "Settings", onClick: () => this.$router.push(`/businesses/${this.businessId}/business`) }, { title: "Business Profile" }];
    },
    operatingModelOptions() {
      return [{ value: "direct", label: "Direct" }, { value: "coordinator", label: "Coordinator" }];
    },
  },
  mounted() {
    this.loadPage();
  },
  methods: {
    ...mapActions("businessActivation", { initBusinessActivation: "init" }),
    refresh() {
      this.loadPage();
    },
    loadPage() {
      this.isProfileLoading = true;
      this.$axios
        .get(`/businesses/${this.businessId}/profile`)
        .then((res) => {
          this.isProfileLoading = false;
          this.name = res.data.name;
          this.types = this.getTypes(res.data.types);
          this.address = res.data.addresses[0] || {};
          this.address.state = this.address.state || "";
          this.operatingModel = res.data.operatingModel;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to load profile",
            message: "We had a problem loading the profile. Please reload the page to try again.",
            err,
          });
        })
        .finally(() => {
          this.isProfileLoading = false;
        });
    },
    getTypes(typeList) {
      let finalList = [];
      for (let t of typeList) {
        finalList.push({ label: t, value: t });
      }

      return finalList;
    },
    save() {
      if (!this.$refs.form.validate()) return;

      this.isProfileSaving = true;

      this.$axios
        .put(`/businesses/${this.businessId}/profile`, {
          name: this.name,
          types: this.types.map((t) => {
            return t.value;
          }),
          addresses: [this.address],
          operatingModel: this.operatingModel,
        })
        .then(() => {
          this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Profile Saved", message: "Your profile was saved successfully" });
          this.initBusinessActivation();
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to save profile",
            message: "We had a problem saving the profile. Please reload the page to try again.",
            err,
          });
        })
        .finally(() => {
          this.isProfileSaving = false;
        });
    },
    upload() {
      this.$refs.modal.show(this.businessId);
    },
  },
};
</script>
